/**
 * What is still owed on each purchase (1.53).
 *
 * ⚠️ `purchases.paid` is what was paid WHEN the goods came, and nothing ever
 * moves it: `pay_supplier` writes a `supplier_payments` row and lowers
 * `suppliers.balance`, and touches no purchase. Reading `total − paid` as the
 * debt therefore kept a purchase „in debt" after the supplier was paid in
 * full — the purchases tab said ₾380 owed while the supplier page said 0, and
 * a shop could pay twice.
 *
 * The supplier's balance is the server's number for the debt (create_purchase
 * adds `total − paid`, pay_supplier takes the payment off, both in one
 * transaction), so it is not re-derived here from the payment rows — it is
 * spread over the supplier's purchases as payments are understood in a shop:
 * a payment clears the OLDEST purchase first, so whatever is still owed sits
 * on the newest. A purchase never shows more than its own unpaid part, and an
 * overpaid supplier (balance below 0, the shop's credit) leaves nothing owed.
 * A purchase with no supplier — or one no longer in the list — has nobody to
 * pay, and keeps its own `total − paid`.
 */
type PurchaseRow = { id: string; supplier_id: string | null; total: number; paid: number; created_at: string }

const tetri = (n: number) => Math.round(n * 100)

type ReturnRow = { purchase_id: string | null; total: number; settle: 'debt' | 'cash' }

/**
 * 170 — a debt-way return linked to a purchase lowers THAT purchase's own
 * unpaid part first; the (already lowered) balance then spreads newest-first
 * as before. Without it, a return from the newest purchase would show as
 * cleared on an old one. A cash-way return moves no debt.
 */
export function purchaseDebts(purchases: PurchaseRow[], suppliers: { id: string; balance: number }[], returns: ReturnRow[] = []): Map<string, number> {
  const balance = new Map(suppliers.map((s) => [s.id, s.balance]))
  const out = new Map<string, number>()
  const bySupplier = new Map<string, PurchaseRow[]>()
  const returned = new Map<string, number>()
  for (const r of returns) {
    if (r.settle === 'debt' && r.purchase_id) returned.set(r.purchase_id, (returned.get(r.purchase_id) ?? 0) + tetri(r.total))
  }
  const ownOf = (p: PurchaseRow) => Math.max(0, tetri(p.total) - tetri(p.paid) - (returned.get(p.id) ?? 0))
  for (const p of purchases) {
    const own = ownOf(p)
    if (p.supplier_id && balance.has(p.supplier_id)) {
      const list = bySupplier.get(p.supplier_id)
      if (list) list.push(p)
      else bySupplier.set(p.supplier_id, [p])
    } else {
      out.set(p.id, own / 100)
    }
  }
  for (const [id, list] of bySupplier) {
    // newest first — the debt that is left belongs to them
    list.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : a.id < b.id ? 1 : a.id > b.id ? -1 : 0))
    let left = Math.max(0, tetri(balance.get(id)!))
    for (const p of list) {
      const take = Math.min(ownOf(p), left)
      out.set(p.id, take / 100)
      left -= take
    }
  }
  return out
}

/**
 * What keeps a supplier from being deleted: an open balance (it would vanish
 * without a trace), a payment history (supplier_payments cascade), or a return
 * (170 restricts the delete — the returns and the losses they booked are the
 * shop's books; a cash-way return leaves the balance at 0, so the balance rule
 * alone would let it through — fresh review I1). Null = it may go.
 */
export function supplierDeleteRefusal(
  s: { id: string; name: string; balance: number },
  payments: { supplier_id: string }[],
  returns: { supplier_id: string }[],
): { key: 'store.x.66'; params: { name: string; p1: string } } | { key: 'store.x.67' | 'store.x.supReturns' } | null {
  if (Math.abs(s.balance) >= 0.005) return { key: 'store.x.66', params: { name: s.name, p1: Math.abs(s.balance).toFixed(2) } }
  if (payments.some((p) => p.supplier_id === s.id)) return { key: 'store.x.67' }
  if (returns.some((r) => r.supplier_id === s.id)) return { key: 'store.x.supReturns' }
  return null
}
